import React, { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Download, Sparkles, Star, Eye, Info, Loader2, History, FileText, Table2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import ThreeJsRibbon from '@/components/ThreeJsRibbon';
import TimeTravelOverlay from '@/components/TimeTravelOverlay';
import PurchasePanel from '@/components/PurchasePanel';
import CelestialSphere from '@/components/CelestialSphere';
import AuthButton from '@/components/AuthButton';
import SignInGate from '@/components/SignInGate';
import { useAuth } from '@/contexts/AuthContext';
import StarTable from '@/components/StarTable';
import GalacticOdometer from '@/components/GalacticOdometer';
import BirthplaceSearch from '@/components/BirthplaceSearchOSM';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZenithPage = () => {
  // Form state
  const [birthDate, setBirthDate] = useState(null);
  const [birthTime, setBirthTime] = useState('02:22');
  const [latitude, setLatitude] = useState('52.4891');
  const [longitude, setLongitude] = useState('13.3085');
  const [timezoneOffset, setTimezoneOffset] = useState('1'); // Default to CET for Berlin
  const [timezoneId, setTimezoneId] = useState('Europe/Berlin'); // IANA timezone
  const [runName, setRunName] = useState('');
  const [placeName, setPlaceName] = useState('');
  const [placeAddress, setPlaceAddress] = useState('');
  const [placeId, setPlaceId] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [skyView, setSkyView] = useState(true);
  const { user, token, claimBand } = useAuth();
  const [gateDismissed, setGateDismissed] = useState(false);
  const [revealDone, setRevealDone] = useState(false);
  const [zenithData, setZenithData] = useState(null);
  const [viewMode, setViewMode] = useState('cosmic');
  const [searchHistory, setSearchHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showStarTable, setShowStarTable] = useState(false);
  const [starTableData, setStarTableData] = useState(null);
  const [constellations, setConstellations] = useState([]);  // Constellation boundary data
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const recordsPerPage = 25;

  // Print image state
  const [printImageStatus, setPrintImageStatus] = useState({});
  const printPollRef = useRef(null);

  const canvasRef = useRef(null);

  // Put the band id in the URL as soon as there is one. Without it a calculated
  // band has no link: no bookmark, no back button, nothing to share — and since
  // history became per-account, an anonymous band would otherwise be lost the
  // moment the tab closes.
  useEffect(() => {
    if (!zenithData?.id) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('band') === zenithData.id) return;
    url.searchParams.set('band', zenithData.id);
    window.history.replaceState({}, '', url);
  }, [zenithData?.id]);

  // A new band deserves the gate again, and a fresh reveal.
  useEffect(() => { setGateDismissed(false); }, [zenithData?.id]);
  useEffect(() => { if (loading) setRevealDone(false); }, [loading]);

  // Open a band directly from a link, e.g. /app?band=<uuid> as used by the
  // Your bands page. Runs once per band id so it cannot loop.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('band');
    if (!wanted || wanted === zenithData?.id) return;
    loadHistoricalRun(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.search]);

  // History belongs to the account, so it must be refetched whenever that
  // changes — otherwise it stays empty after signing in, or keeps showing the
  // previous account's bands after signing out.
  useEffect(() => {
    fetchHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Bands made or opened while signed in get attached to the account. The server
  // only claims a band with no owner, so this is safe to fire on any band.
  useEffect(() => {
    if (user && zenithData?.id) claimBand(zenithData.id);
  }, [user, zenithData?.id, claimBand]);

  // Set default date and fetch constellations
  useEffect(() => {
    setBirthDate(new Date(1974, 5, 22));
    fetchHistory(1);
    fetchConstellations();
  }, []);

  // Fetch constellation boundaries (once on mount)
  const fetchConstellations = async () => {
    try {
      const response = await axios.get(`${API}/constellations/boundaries`);
      if (response.data?.constellations) {
        setConstellations(response.data.constellations);
        console.log(`Loaded ${response.data.count} constellation boundaries`);
      }
    } catch (error) {
      console.warn('Could not load constellation boundaries:', error.message);
      // Non-blocking - app works without constellations
    }
  };

  // Handle location selection from BirthplaceSearch
  const handleLocationSelect = useCallback((placeData) => {
    if (placeData) {
      setLatitude(placeData.lat.toString());
      setLongitude(placeData.lng.toString());
      setPlaceName(placeData.name || '');
      setPlaceAddress(placeData.formatted_address || '');
      setPlaceId(placeData.place_id || '');
      
      // Set timezone ID and offset
      if (placeData.timezone) {
        setTimezoneId(placeData.timezone.timeZoneId || 'UTC');
        setTimezoneOffset(placeData.timezone.totalOffset?.toString() || '0');
        console.log('Timezone set:', placeData.timezone.timeZoneId, 'offset:', placeData.timezone.totalOffset);
      }
    } else {
      setPlaceName('');
      setPlaceAddress('');
      setPlaceId('');
    }
  }, []);
  
  const fetchHistory = async (page = 1) => {
    setLoadingHistory(true);
    try {
      const skip = (page - 1) * recordsPerPage;
      // History is scoped to the account server-side, so the token has to go
      // with the request or the response is legitimately empty.
      const response = await axios.get(
        `${API}/zenith/history?skip=${skip}&limit=${recordsPerPage}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      setSearchHistory(response.data.history || []);
      setTotalRecords(response.data.total || 0);
      setHasMore(response.data.has_more || false);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load history');
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchHistory(currentPage - 1);
    }
  };
  
  const handleNextPage = () => {
    if (hasMore) {
      fetchHistory(currentPage + 1);
    }
  };

  const handleCalculate = async () => {
    if (!birthDate || !birthTime || !latitude || !longitude) {
      toast.error('Please fill in all required fields');
      return;
    }

    console.log('=== CALCULATE DEBUG ===');
    console.log('Birth Date:', birthDate);
    console.log('Birth Time:', birthTime);
    console.log('Latitude:', latitude);
    console.log('Longitude:', longitude);
    console.log('Timezone Offset:', timezoneOffset);
    console.log('Place Name:', placeName);
    
    // Format birthDate properly (it's a Date object)
    const year = birthDate.getFullYear();
    const month = String(birthDate.getMonth() + 1).padStart(2, '0');
    const day = String(birthDate.getDate()).padStart(2, '0');
    const birthDateStr = `${year}-${month}-${day}`;
    const birthDateTime = `${birthDateStr}T${birthTime}:00`;
    
    console.log('Formatted birth_datetime:', birthDateTime);
    console.log('Sending timezone_id:', timezoneId);
    console.log('Sending timezone_offset:', parseFloat(timezoneOffset));

    setLoading(true);
    
    try {
      const dateStr = format(birthDate, 'yyyy-MM-dd');
      const birthDatetime = `${dateStr}T${birthTime}:00`;
      
      const response = await axios.post(`${API}/zenith/calculate`, {
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        birth_datetime: birthDatetime,
        timezone_id: timezoneId,
        timezone_offset: parseFloat(timezoneOffset),
        ribbon_minutes: 2,
        run_name: runName || null,
        place_name: placeName || null,
        place_address: placeAddress || null,
        place_id: placeId || null
      });
      
      setZenithData(response.data);
      toast.success(`Discovered ${response.data.star_count} celestial objects!`);
      
      // Refresh history - go back to page 1 to see the new entry
      fetchHistory(1);
      
    } catch (error) {
      console.error('Calculation error:', error);
      
      // Handle validation errors (422)
      if (error.response?.status === 422 && error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation error format
          const errorMessages = detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Invalid input. Please check your form fields.');
        }
      } else {
        toast.error(error.response?.data?.detail || error.message || 'Failed to calculate star band');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadHistoricalRun = async (bandId) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/zenith/band/${bandId}`);
      setZenithData(response.data);
      toast.success('Loaded historical run');
    } catch (error) {
      console.error('Failed to load run:', error);
      toast.error('Failed to load historical run');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDownload = useCallback(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `birth-zenith-${zenithData?.run_name || format(birthDate, 'yyyy-MM-dd')}.png`;
    link.href = canvas.toDataURL('image/png', 1.0);
    link.click();
    toast.success('Star band downloaded!');
  }, [birthDate, zenithData]);

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Unknown';
    const date = new Date(isoString);
    return format(date, 'MMM dd, HH:mm');
  };


  // Print image generation
  const startPrintPoll = useCallback(() => {
    if (printPollRef.current) return; // already polling
    printPollRef.current = setInterval(async () => {
      if (!zenithData?.id) return;
      try {
        const resp = await axios.get(`${API}/zenith/band/${zenithData.id}/survey-images`);
        const statusMap = {};
        for (const img of (resp.data.images || [])) {
          statusMap[img.image_id] = img;
        }
        setPrintImageStatus(statusMap);
        // Stop polling if all are complete or failed
        const statuses = Object.values(statusMap);
        if (statuses.length > 0 && statuses.every(s => s.status === 'complete' || s.status === 'failed')) {
          clearInterval(printPollRef.current);
          printPollRef.current = null;
          const completed = statuses.filter(s => s.status === 'complete').length;
          if (completed > 0) toast.success(`${completed} print image(s) ready for download`);
        }
      } catch (err) {
        console.warn('Print poll error:', err.message);
      }
    }, 2000);
  }, [zenithData?.id]);

  const handleGeneratePrint = useCallback(async (surveyId, preset, batchAll) => {
    if (!zenithData?.id) return;
    try {
      await axios.post(`${API}/zenith/band/${zenithData.id}/survey-image`, {
        survey_id: surveyId,
        print_preset: preset,
        batch_all: batchAll,
        format: 'png',
        dpi: 300,
      });
      toast.success(batchAll ? 'Generating all 9 surveys...' : 'Generating print image...');
      // Start polling for status
      startPrintPoll();
    } catch (error) {
      console.error('Failed to start print generation:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate print image');
    }
  }, [zenithData?.id, startPrintPoll]);

  // Cleanup poll on unmount or band change
  useEffect(() => {
    return () => {
      if (printPollRef.current) {
        clearInterval(printPollRef.current);
        printPollRef.current = null;
      }
    };
  }, []);

  // Reset print status when band changes
  useEffect(() => {
    setPrintImageStatus({});
    if (printPollRef.current) {
      clearInterval(printPollRef.current);
      printPollRef.current = null;
    }
    // Load existing images for this band
    if (zenithData?.id) {
      axios.get(`${API}/zenith/band/${zenithData.id}/survey-images`)
        .then(resp => {
          const statusMap = {};
          for (const img of (resp.data.images || [])) {
            statusMap[img.image_id] = img;
          }
          setPrintImageStatus(statusMap);
        })
        .catch(() => {}); // non-blocking
    }
  }, [zenithData?.id]);

  // Open star table
  const openStarTable = (stars, runName, metadata = null) => {
    const birthDate = metadata?.epoch_date || zenithData?.metadata?.epoch_date || null;
    const placeAddress = zenithData?.place_address || zenithData?.place_name;
    setStarTableData({ stars, runName, birthDate, placeAddress });
    setShowStarTable(true);
  };

  const closeStarTable = () => {
    setShowStarTable(false);
    setStarTableData(null);
  };

  // Load a historical run and immediately open its star table
  const loadAndOpenStarTable = async (bandId, e) => {
    e.stopPropagation();
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/zenith/band/${bandId}`);
      const data = response.data;
      setZenithData(data);
      const birthDate = data.metadata?.epoch_date || null;
      const placeAddress = data.place_address || data.place_name;
      setStarTableData({ stars: data.celestial_objects || data.stars, runName: data.run_name, birthDate, placeAddress });
      setShowStarTable(true);
    } catch (error) {
      console.error('Failed to load run:', error);
      toast.error('Failed to load star table');
    } finally {
      setLoadingHistory(false);
    }
  };


  return (
    <div className="min-h-screen bg-[#020204] relative overflow-hidden">
      <div className="hero-gradient absolute inset-0 pointer-events-none" />

      {/* Shown after the reveal, never before it: the band is visible behind the
          panel, so the ask is to keep something they can already see. */}
      <SignInGate
        open={!!zenithData?.id && !loading && revealDone && !gateDismissed}
        onSkip={() => setGateDismissed(true)}
      />

      {/* Occupies the calculate wait: ~7.7s Fly cold wake plus the Gaia query. */}
      <TimeTravelOverlay
        active={loading}
        birthDate={birthDate}
        latitude={latitude}
        longitude={longitude}
        onDone={() => setRevealDone(true)}
      />
      
      {/* Header */}
      <header className="relative z-30 px-6 md:px-12 py-8">
        <div className="flex items-center justify-between gap-4">
          {/* The lockup is the way home — the conventional place people look. */}
          <Link
            to="/"
            aria-label="Stars at Birth — back to home"
            className="flex items-center gap-3 rounded-lg transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#020204]"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-unbounded font-bold text-xl text-white">Stars at Birth</h1>
              <p className="text-xs text-slate-400 font-mono tracking-wider">YOUR COSMIC FINGERPRINT</p>
            </div>
          </Link>

          <div className="flex items-center gap-2 shrink-0">
          <AuthButton />

          {/* Stated outright as well, because the clickable logo alone is a
              convention people miss when there is no other navigation. */}
          <Link
            to="/"
            className="shrink-0 font-mono text-[11px] tracking-[0.18em] uppercase text-slate-400 hover:text-cyan-300 transition-colors border border-white/10 hover:border-cyan-400/40 rounded-full px-4 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
          >
            &larr; Home
          </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10 px-6 md:px-12 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Hero text */}
          <div className="mb-12 max-w-3xl animate-fade-in-up">
            <h2 className="font-unbounded font-bold text-4xl md:text-5xl text-white mb-6 glow-text leading-tight">
              The Stars Above You<br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-cyan-200 to-indigo-300">
                At Your First Breath
              </span>
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed">
              Discover the exact 1.0° x 0.2° ribbon of cosmos that was directly overhead at your birth.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Input Form */}
            <div className="lg:col-span-1">
              <div className="form-panel p-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                <h3 className="font-unbounded font-semibold text-lg text-white mb-6 flex items-center gap-2">
                  <Star className="w-5 h-5 text-cyan-400" />
                  Birth Coordinates
                </h3>

                {/* Run Name */}
                <div className="mb-5">
                  <label className="label-cosmic">Run Name (Optional)</label>
                  <Input
                    type="text"
                    value={runName}
                    onChange={(e) => setRunName(e.target.value)}
                    data-testid="run-name-input"
                    placeholder="e.g., My Birthday Stars"
                    className="bg-black/30 border-white/15 focus:border-cyan-400/50 text-white"
                  />
                </div>

                {/* Date Picker */}
                <div className="mb-5">
                  <label className="label-cosmic">Birth Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="birth-date-trigger"
                        className="w-full justify-start text-left font-normal bg-black/30 border-white/15 hover:bg-black/50 hover:border-cyan-400/50 text-white"
                      >
                        <Calendar className="mr-2 h-4 w-4 text-cyan-400" />
                        {birthDate ? format(birthDate, 'PPP') : <span className="text-white/30">Select date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0a0a0f] border-white/10" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={birthDate}
                        onSelect={setBirthDate}
                        data-testid="birth-date-calendar"
                        initialFocus
                        captionLayout="dropdown-buttons"
                        fromYear={1900}
                        toYear={new Date().getFullYear()}
                        className="bg-[#0a0a0f]"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time Picker */}
                <div className="mb-5">
                  <label className="label-cosmic">Birth Time (Local)</label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400" />
                    <Input
                      type="time"
                      value={birthTime}
                      onChange={(e) => setBirthTime(e.target.value)}
                      data-testid="birth-time-input"
                      className="pl-10 bg-black/30 border-white/15 focus:border-cyan-400/50 text-white"
                    />
                  </div>
                </div>

                {/* Birthplace Search with Google Maps */}
                <div className="mb-5">
                  <BirthplaceSearch
                    onLocationSelect={handleLocationSelect}
                    birthDate={birthDate && birthTime ? `${format(birthDate, 'yyyy-MM-dd')}T${birthTime}:00` : null}
                    initialLat={parseFloat(latitude)}
                    initialLng={parseFloat(longitude)}
                  />
                </div>

                {/* Timezone is now auto-detected - hidden from UI but still used in calculation */}

                {/* Calculate Button */}
                <Button
                  onClick={handleCalculate}
                  disabled={loading}
                  data-testid="calculate-btn"
                  className="w-full btn-glow bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 text-white font-unbounded font-medium py-6 rounded-xl"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Mapping the Cosmos...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Star Band
                    </>
                  )}
                </Button>

                {loading && (
                  <p className="text-xs text-center text-slate-500 mt-3 font-mono">
                    Querying Potsdam Gaia@AIP Archive...
                  </p>
                )}
              </div>
            </div>

            {/* Visualization Area */}
            <div className="lg:col-span-2">
              {zenithData ? (
                <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  {/* Run Name Display */}
                  {zenithData.run_name && (
                    <div className="mb-4">
                      <span className="text-xs font-mono text-slate-500">RUN NAME:</span>
                      <h3 className="font-unbounded text-xl text-white">{zenithData.run_name}</h3>
                    </div>
                  )}

                  {/* View Toggle */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <Eye className="w-5 h-5 text-slate-400" />
                      <div className="toggle-cosmic flex">
                        <div 
                          className="toggle-cosmic-slider"
                          style={{ transform: viewMode === 'cosmic' ? 'translateX(0)' : 'translateX(100%)' }}
                        />
                        <button
                          onClick={() => setViewMode('cosmic')}
                          data-testid="toggle-cosmic"
                          className={`toggle-cosmic-option ${viewMode === 'cosmic' ? 'text-white' : 'text-slate-400'}`}
                        >
                          Cosmic
                        </button>
                        <button
                          onClick={() => setViewMode('minimalist')}
                          data-testid="toggle-minimalist"
                          className={`toggle-cosmic-option ${viewMode === 'minimalist' ? 'text-white' : 'text-slate-400'}`}
                        >
                          Minimal
                        </button>
                      </div>
                    </div>

                    <Button
                      onClick={handleDownload}
                      data-testid="download-btn"
                      className="download-btn"
                    >
                      <Download className="w-4 h-4" />
                      Download PNG
                    </Button>
                  </div>

                  {/* Sky vs ribbon. The sphere is the default because it is the
                      view that conveys scale; the ribbon stays for the print crop. */}
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      onClick={() => setSkyView(true)}
                      className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-wider transition-colors ${
                        skyView ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40'
                                : 'text-slate-400 border border-white/10 hover:text-white'}`}
                    >
                      SKY
                    </button>
                    <button
                      onClick={() => setSkyView(false)}
                      className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-wider transition-colors ${
                        !skyView ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40'
                                 : 'text-slate-400 border border-white/10 hover:text-white'}`}
                    >
                      RIBBON
                    </button>
                  </div>

                  {skyView ? (
                    <CelestialSphere
                      stars={zenithData.celestial_objects || zenithData.stars}
                      constellations={constellations}
                      zenithRa={zenithData.zenith_ra}
                      zenithDec={zenithData.zenith_dec}
                      ribbonBounds={zenithData.ribbon_bounds}
                      birthDatetime={zenithData.birth_datetime}
                      bandId={zenithData.id}
                    />
                  ) : (
                  <ThreeJsRibbon
                    stars={zenithData.celestial_objects || zenithData.stars}
                    zenithRa={zenithData.zenith_ra}
                    zenithDec={zenithData.zenith_dec}
                    ribbonBounds={zenithData.ribbon_bounds}
                    constellations={constellations}
                    bandId={zenithData.id}
                    onGeneratePrintImage={handleGeneratePrint}
                    printImageStatus={printImageStatus}
                  />
                  )}

                  {/* Buy the keepable versions. The on-screen PNG stays free. */}
                  <PurchasePanel bandId={zenithData.id} />

                  {/* Cosmic Message */}
                  <div className="mt-6 p-5 glass rounded-xl">
                    <p className="text-slate-300 text-sm leading-relaxed italic">
                      "{zenithData.cosmic_message}"
                    </p>
                  </div>

                  {/* Notable Objects */}
                  {zenithData.notable_objects && zenithData.notable_objects.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-unbounded font-medium text-white mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-pink-400" />
                        Notable Objects in Your Band
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {zenithData.notable_objects.map((obj, idx) => (
                          <div key={idx} className="notable-badge">
                            <span className="font-medium">{obj.common_name}</span>
                            <span className="text-pink-300/70">({obj.object_type})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Constellation Summary - Featured */}
                  {zenithData.constellation_summary && (
                    <div className="mt-6 p-5 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-400/30 rounded-xl">
                      <div className="flex items-center gap-3 mb-2">
                        <Sparkles className="w-5 h-5 text-indigo-300" />
                        <h4 className="font-unbounded font-medium text-white">Constellation</h4>
                      </div>
                      <p className="text-2xl font-unbounded font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 to-purple-200">
                        {zenithData.constellation_summary}
                      </p>
                      <p className="text-xs text-indigo-300/70 mt-2">
                        Your birth zenith ribbon is located in this celestial region
                      </p>
                    </div>
                  )}

                  {/* Galactic Odometer - NEW */}
                  {zenithData.metadata?.epoch_date && (
                    <GalacticOdometer birthDate={zenithData.metadata.epoch_date} />
                  )}

                  {/* Metadata Grid */}
                  <div className="metadata-grid mt-8">
                    <div className="metadata-item">
                      <p className="label-cosmic mb-1">Zenith RA</p>
                      <p className="font-mono text-lg text-white">{zenithData.zenith_ra.toFixed(4)}°</p>
                    </div>
                    <div className="metadata-item">
                      <p className="label-cosmic mb-1">Zenith Dec</p>
                      <p className="font-mono text-lg text-white">{zenithData.zenith_dec.toFixed(4)}°</p>
                    </div>
                    <div
                      className="metadata-item col-span-2"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="label-cosmic">Celestial Objects for <span className="text-cyan-300">{zenithData.run_name || zenithData.place_name || 'Unknown'}</span></p>
                        <button
                          onClick={() => openStarTable(zenithData.celestial_objects || zenithData.stars, zenithData.run_name, zenithData.metadata)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs rounded-lg transition-colors font-medium"
                          title="Open interactive star table"
                        >
                          <Table2 className="w-3.5 h-3.5" />
                          View Star Table
                        </button>
                      </div>
                      <p className="label-cosmic text-xs mb-3 text-slate-400">
                        {zenithData.place_address || zenithData.place_name || 'Location'} • {new Date(zenithData.metadata?.epoch_date || zenithData.created_at).toLocaleString()}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="font-mono text-2xl text-cyan-400 hover:text-cyan-300">
                          {(zenithData.object_count || zenithData.star_count || 0).toLocaleString()}
                        </span>
                        <span className="text-slate-400 text-sm self-end mb-1">total objects</span>
                      </div>
                      {zenithData.debug_audit?.classification_counts && (
                        <div className="flex flex-wrap gap-2 mt-2 text-xs">
                          <span className="px-2 py-1 bg-indigo-500/20 text-indigo-300 rounded">
                            {zenithData.debug_audit.classification_counts.Star?.toLocaleString() || 0} Stars
                          </span>
                          {zenithData.debug_audit.classification_counts.Quasar > 0 && (
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded">
                              {zenithData.debug_audit.classification_counts.Quasar} Quasars
                            </span>
                          )}
                          {zenithData.debug_audit.classification_counts.Galaxy > 0 && (
                            <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded">
                              {zenithData.debug_audit.classification_counts.Galaxy} Galaxies
                            </span>
                          )}
                        </div>
                      )}
                      {zenithData.debug_audit?.catalog_counts && (
                        <div className="flex flex-wrap gap-2 mt-2 text-xs">
                          {zenithData.debug_audit.catalog_counts.hip > 0 && (
                            <span className="px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded">
                              {zenithData.debug_audit.catalog_counts.hip} HIP
                            </span>
                          )}
                          {zenithData.debug_audit.catalog_counts.tyc > 0 && (
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded">
                              {zenithData.debug_audit.catalog_counts.tyc} TYC
                            </span>
                          )}
                          {zenithData.debug_audit.catalog_counts.tmass > 0 && (
                            <span className="px-2 py-1 bg-orange-500/20 text-orange-300 rounded">
                              {zenithData.debug_audit.catalog_counts.tmass.toLocaleString()} 2MASS
                            </span>
                          )}
                          {zenithData.debug_audit.catalog_counts.wise > 0 && (
                            <span className="px-2 py-1 bg-pink-500/20 text-pink-300 rounded">
                              {zenithData.debug_audit.catalog_counts.wise.toLocaleString()} WISE
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="metadata-item">
                      <p className="label-cosmic mb-1">Named Objects</p>
                      <p className="font-mono text-lg text-amber-400">{zenithData.named_object_count || zenithData.named_star_count || 0}</p>
                    </div>
                    <div className="metadata-item">
                      <p className="label-cosmic mb-1">Local Sidereal Time</p>
                      <p className="font-mono text-lg text-white">{zenithData.metadata?.lst_hours?.toFixed(4) || 'N/A'}h</p>
                    </div>
                    <div className="metadata-item">
                      <p className="label-cosmic mb-1">Field of View</p>
                      <p className="font-mono text-lg text-white">{zenithData.metadata?.fov_degrees || 'N/A'}°</p>
                    </div>
                    <div className="metadata-item">
                      <p className="label-cosmic mb-1">Epoch</p>
                      <p className="font-mono text-lg text-white">{zenithData.metadata?.epoch_date || 'N/A'}</p>
                    </div>
                    <div className="metadata-item">
                      <p className="label-cosmic mb-1">Data Source</p>
                      <p className="font-mono text-sm text-cyan-400">{zenithData.metadata?.star_data_source || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Debug Audit Panel */}
                  {zenithData.debug_audit && (
                    <div className="mt-8 p-4 bg-black/50 border border-amber-500/30 rounded-xl">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-mono text-amber-400 text-sm flex items-center gap-2">
                          <Info className="w-4 h-4" />
                          DEBUG AUDIT
                        </h4>
                        <a
                          href={`${BACKEND_URL}/api/zenith/band/${zenithData.id}/csv`}
                          download
                          data-testid="download-csv-btn"
                          className="text-xs bg-cyan-600 hover:bg-cyan-500 text-white px-3 py-1.5 rounded flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          Download CSV for Aladin
                        </a>
                      </div>
                      
                      {/* Object Type Breakdown - Dynamic from classification */}
                      {zenithData.object_type_breakdown && (
                        <div className="mb-4 p-3 bg-indigo-500/10 border border-indigo-500/30 rounded">
                          <p className="text-xs text-indigo-300 mb-2 font-mono">OBJECT CLASSIFICATION (Gaia DSC &gt;0.5 threshold):</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(zenithData.object_type_breakdown).map(([type, count]) => (
                              <span 
                                key={type} 
                                className={`text-xs px-2 py-1 rounded ${
                                  type === 'Quasar' ? 'bg-purple-500/30 text-purple-200' :
                                  type === 'Galaxy' ? 'bg-pink-500/30 text-pink-200' :
                                  'bg-indigo-500/20 text-indigo-200'
                                }`}
                              >
                                {count.toLocaleString()} {type}{count !== 1 ? 's' : ''}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Catalog Cross-References */}
                      {zenithData.debug_audit?.catalog_counts && (
                        <div className="mb-4 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded">
                          <p className="text-xs text-cyan-300 mb-2 font-mono">CATALOG CROSS-REFERENCES (from Gaia):</p>
                          <div className="flex flex-wrap gap-2">
                            {zenithData.debug_audit.catalog_counts.hip > 0 && (
                              <span className="text-xs px-2 py-1 rounded bg-cyan-500/30 text-cyan-200">
                                <strong>{zenithData.debug_audit.catalog_counts.hip.toLocaleString()}</strong> HIP
                              </span>
                            )}
                            {zenithData.debug_audit.catalog_counts.tyc > 0 && (
                              <span className="text-xs px-2 py-1 rounded bg-purple-500/30 text-purple-200">
                                <strong>{zenithData.debug_audit.catalog_counts.tyc.toLocaleString()}</strong> TYC
                              </span>
                            )}
                            {zenithData.debug_audit.catalog_counts.tmass > 0 && (
                              <span className="text-xs px-2 py-1 rounded bg-orange-500/30 text-orange-200">
                                <strong>{zenithData.debug_audit.catalog_counts.tmass.toLocaleString()}</strong> 2MASS
                              </span>
                            )}
                            {zenithData.debug_audit.catalog_counts.wise > 0 && (
                              <span className="text-xs px-2 py-1 rounded bg-pink-500/30 text-pink-200">
                                <strong>{zenithData.debug_audit.catalog_counts.wise.toLocaleString()}</strong> WISE
                              </span>
                            )}
                            {zenithData.debug_audit.catalog_counts.panstarrs > 0 && (
                              <span className="text-xs px-2 py-1 rounded bg-green-500/30 text-green-200">
                                <strong>{zenithData.debug_audit.catalog_counts.panstarrs.toLocaleString()}</strong> Pan-STARRS
                              </span>
                            )}
                            {zenithData.debug_audit.catalog_counts.sdss > 0 && (
                              <span className="text-xs px-2 py-1 rounded bg-blue-500/30 text-blue-200">
                                <strong>{zenithData.debug_audit.catalog_counts.sdss.toLocaleString()}</strong> SDSS
                              </span>
                            )}
                            {zenithData.debug_audit.catalog_counts.gsc > 0 && (
                              <span className="text-xs px-2 py-1 rounded bg-indigo-500/30 text-indigo-200">
                                <strong>{zenithData.debug_audit.catalog_counts.gsc.toLocaleString()}</strong> GSC
                              </span>
                            )}
                            {zenithData.debug_audit.catalog_counts.apass > 0 && (
                              <span className="text-xs px-2 py-1 rounded bg-teal-500/30 text-teal-200">
                                <strong>{zenithData.debug_audit.catalog_counts.apass.toLocaleString()}</strong> APASS
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Spectral Type Breakdown with Percentages */}
                      {zenithData.debug_audit?.spectral_counts && (
                        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded">
                          <p className="text-xs text-amber-300 mb-2 font-mono">SPECTRAL CLASSIFICATION (from Gaia temperature/color):</p>
                          <div className="flex flex-wrap gap-2">
                            {(() => {
                              const total = zenithData.debug_audit.spectral_counts.total_with_spectral || 1;
                              const sc = zenithData.debug_audit.spectral_counts;
                              return (
                                <>
                                  {sc.O > 0 && (
                                    <span className="text-xs px-2 py-1 rounded bg-blue-600/40 text-blue-200">
                                      <strong>{sc.O}</strong> O <span className="opacity-70">({(sc.O / total * 100).toFixed(1)}%)</span>
                                    </span>
                                  )}
                                  {sc.B > 0 && (
                                    <span className="text-xs px-2 py-1 rounded bg-blue-500/40 text-blue-200">
                                      <strong>{sc.B}</strong> B <span className="opacity-70">({(sc.B / total * 100).toFixed(1)}%)</span>
                                    </span>
                                  )}
                                  {sc.A > 0 && (
                                    <span className="text-xs px-2 py-1 rounded bg-gray-200/40 text-white">
                                      <strong>{sc.A}</strong> A <span className="opacity-70">({(sc.A / total * 100).toFixed(1)}%)</span>
                                    </span>
                                  )}
                                  {sc.F > 0 && (
                                    <span className="text-xs px-2 py-1 rounded bg-yellow-300/40 text-yellow-100">
                                      <strong>{sc.F}</strong> F <span className="opacity-70">({(sc.F / total * 100).toFixed(1)}%)</span>
                                    </span>
                                  )}
                                  {sc.G > 0 && (
                                    <span className="text-xs px-2 py-1 rounded bg-yellow-500/40 text-yellow-200">
                                      <strong>{sc.G.toLocaleString()}</strong> G <span className="opacity-70">({(sc.G / total * 100).toFixed(1)}%)</span>
                                    </span>
                                  )}
                                  {sc.K > 0 && (
                                    <span className="text-xs px-2 py-1 rounded bg-orange-500/40 text-orange-200">
                                      <strong>{sc.K.toLocaleString()}</strong> K <span className="opacity-70">({(sc.K / total * 100).toFixed(1)}%)</span>
                                    </span>
                                  )}
                                  {sc.M > 0 && (
                                    <span className="text-xs px-2 py-1 rounded bg-red-500/40 text-red-200">
                                      <strong>{sc.M.toLocaleString()}</strong> M <span className="opacity-70">({(sc.M / total * 100).toFixed(1)}%)</span>
                                    </span>
                                  )}
                                  <span className="text-xs px-2 py-1 rounded bg-slate-500/30 text-slate-200">
                                    Total: <strong>{sc.total_with_spectral?.toLocaleString()}</strong>
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {/* Classification info note */}
                      <div className="mb-4 p-2 bg-slate-500/10 border border-slate-500/20 rounded text-xs text-slate-400">
                        <strong>Classification:</strong> Uses Gaia DSC (Data Science Collaboration) probabilities. 
                        Objects with &gt;50% quasar/galaxy probability are classified accordingly. 
                        P1 backlog: IAU star names cross-match for common_name field.
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-mono">
                        <div>
                          <p className="text-slate-500">HTTP Status</p>
                          <p className={zenithData.debug_audit.http_status_code === 200 ? 'text-green-400' : 'text-red-400'}>
                            {zenithData.debug_audit.http_status_code}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Response Time</p>
                          <p className="text-white">{zenithData.debug_audit.api_response_time_ms?.toFixed(0)}ms</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Response Size</p>
                          <p className="text-white">{(zenithData.debug_audit.raw_response_length_bytes / 1024).toFixed(1)}KB</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Rows Received</p>
                          <p className="text-white">{zenithData.debug_audit.total_rows_received}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Query RA Range</p>
                          <p className="text-white">[{zenithData.debug_audit.query_ra_min?.toFixed(2)}, {zenithData.debug_audit.query_ra_max?.toFixed(2)}]</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Query Dec Range</p>
                          <p className="text-white">[{zenithData.debug_audit.query_dec_min?.toFixed(2)}, {zenithData.debug_audit.query_dec_max?.toFixed(2)}]</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Mag Limit</p>
                          <p className="text-white">&lt;{zenithData.debug_audit.query_mag_limit}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Rows After Filter</p>
                          <p className="text-white">{zenithData.debug_audit.rows_after_filter}</p>
                        </div>
                      </div>
                      {zenithData.debug_audit.error_message && (
                        <div className="mt-4 p-2 bg-red-500/20 rounded text-red-400 text-xs">
                          {zenithData.debug_audit.error_message}
                        </div>
                      )}
                      <div className="mt-4">
                        <p className="text-slate-500 text-xs mb-2">SQL Query Sent:</p>
                        <pre className="text-xs text-cyan-300 bg-black/50 p-2 rounded overflow-x-auto whitespace-pre-wrap">
                          {zenithData.debug_audit.raw_sql_sent}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Empty State */
                <div className="h-full min-h-[500px] flex flex-col items-center justify-center text-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 flex items-center justify-center mb-6 animate-pulse-glow">
                    <Star className="w-10 h-10 text-indigo-400" />
                  </div>
                  <h3 className="font-unbounded font-semibold text-xl text-white mb-3">
                    Your Cosmic Fingerprint Awaits
                  </h3>
                  <p className="text-slate-400 max-w-md">
                    Enter your birth coordinates and time to reveal the exact stars 
                    that were directly above you at the moment of your first breath.
                  </p>
                  
 
                </div>
              )}
            </div>
          </div>

          {/* Search History Section */}
          <div className="mt-16 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-unbounded font-semibold text-xl text-white flex items-center gap-3">
                <History className="w-6 h-6 text-indigo-400" />
                Search History
              </h3>
              <div className="text-sm text-slate-400 font-mono">
                {totalRecords > 0 && (
                  <span>
                    Showing {((currentPage - 1) * recordsPerPage) + 1}-{Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords.toLocaleString()} runs
                  </span>
                )}
              </div>
            </div>
            
            {loadingHistory ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 mx-auto mb-4 text-cyan-400 animate-spin" />
                <p className="text-slate-400">Loading history...</p>
              </div>
            ) : searchHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Run Time</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Run Name</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Birth Date</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Birth Time</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">TZ</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Location</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Zenith RA</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Zenith Dec</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">GPS</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Constellation</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Total Stars</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Famous Stars</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Duration</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">API</th>
                      <th className="text-left py-3 px-3 font-mono text-xs text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchHistory.map((run, idx) => (
                      <tr 
                        key={run.id}
                        onClick={() => loadHistoricalRun(run.id)}
                        className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                        data-testid={`history-row-${idx}`}
                      >
                        <td className="py-3 px-3 font-mono text-slate-300 text-xs">
                          {formatDateTime(run.created_at)}
                        </td>
                        <td className="py-3 px-3 text-white text-xs">
                          {run.run_name || <span className="text-slate-500 italic">Untitled</span>}
                        </td>
                        <td className="py-3 px-3 font-mono text-slate-400 text-xs">
                          {run.birth_datetime ? run.birth_datetime.split('T')[0] : 'N/A'}
                        </td>
                        <td className="py-3 px-3 font-mono text-cyan-400 text-xs">
                          {run.birth_datetime ? run.birth_datetime.split('T')[1]?.substring(0, 5) : 'N/A'}
                        </td>
                        <td className="py-3 px-3 font-mono text-amber-400 text-xs">
                          {run.timezone_offset !== undefined ? `${run.timezone_offset > 0 ? '+' : ''}${run.timezone_offset}` : 'N/A'}
                        </td>
                        <td className="py-3 px-3 text-xs max-w-xs">
                          {run.place_name || run.place_address ? (
                            <div className="truncate" title={run.place_address || run.place_name}>
                              <span className="text-cyan-300">{run.place_name || 'Unknown'}</span>
                              {run.place_address && run.place_address !== run.place_name && (
                                <span className="text-slate-500 text-xs block truncate">
                                  {run.place_address}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-500 italic">No address</span>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-amber-400 text-xs" title="Right Ascension at Zenith">
                          {run.zenith_ra ? `${run.zenith_ra.toFixed(2)}°` : 'N/A'}
                        </td>
                        <td className="py-3 px-3 font-mono text-amber-400 text-xs" title="Declination at Zenith">
                          {run.zenith_dec ? `${run.zenith_dec.toFixed(2)}°` : 'N/A'}
                        </td>
                        <td className="py-3 px-3 font-mono text-xs">
                          {run.latitude && run.longitude ? (
                            <a
                              href={`https://www.google.com/maps?q=${run.latitude},${run.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-cyan-400 hover:text-cyan-300 hover:underline"
                            >
                              {run.latitude?.toFixed(2)}°, {run.longitude?.toFixed(2)}°
                            </a>
                          ) : (
                            <span className="text-slate-500">N/A</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-xs">
                          {run.constellation_summary ? (
                            <span className="px-2 py-1 rounded bg-indigo-500/20 text-indigo-300 font-medium">
                              {run.constellation_summary}
                            </span>
                          ) : (
                            <span className="text-slate-500 italic">N/A</span>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-white text-xs font-medium">
                          {run.object_count?.toLocaleString() || run.star_count?.toLocaleString() || 'N/A'}
                        </td>
                        <td className="py-3 px-3 font-mono text-yellow-400 text-xs font-medium" title="Stars with proper names, Bayer designations, or HD/HIP catalog IDs">
                          {run.named_object_count ? (
                            <span className="flex items-center gap-1">
                              ⭐ {run.named_object_count.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-slate-500">0</span>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-slate-400 text-xs">
                          {run.debug_audit?.api_response_time_ms 
                            ? `${(run.debug_audit.api_response_time_ms / 1000).toFixed(1)}s` 
                            : 'N/A'}
                        </td>
                        <td className="py-3 px-3 text-xs">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            run.data_source?.includes('Potsdam') 
                              ? 'bg-green-500/20 text-green-400' 
                              : run.data_source?.includes('ESA')
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-slate-500/20 text-slate-400'
                          }`}>
                            {run.data_source?.includes('Potsdam') ? 'Potsdam' : 
                             run.data_source?.includes('ESA') ? 'ESA' : 
                             run.data_source || 'Unknown'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={(e) => loadAndOpenStarTable(run.id, e)}
                              className="text-xs bg-indigo-700 hover:bg-indigo-600 text-white px-2 py-1 rounded inline-flex items-center gap-1"
                              title="View star table for this run"
                            >
                              <Table2 className="w-3 h-3" />
                              Stars
                            </button>
                            <a
                              href={`${BACKEND_URL}/api/zenith/band/${run.id}/csv`}
                              download
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-2 py-1 rounded inline-flex items-center gap-1"
                            >
                              <Download className="w-3 h-3" />
                              CSV
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Pagination Controls */}
                {totalRecords > recordsPerPage && (
                  <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-6">
                    <div className="text-sm text-slate-400 font-mono">
                      Page {currentPage} of {Math.ceil(totalRecords / recordsPerPage)}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded font-mono text-sm transition-colors ${
                          currentPage === 1
                            ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                            : 'bg-slate-700 text-white hover:bg-slate-600'
                        }`}
                      >
                        ← Previous
                      </button>
                      <button
                        onClick={handleNextPage}
                        disabled={!hasMore}
                        className={`px-4 py-2 rounded font-mono text-sm transition-colors ${
                          !hasMore
                            ? 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                            : 'bg-slate-700 text-white hover:bg-slate-600'
                        }`}
                      >
                        Next →
                      </button>
                    </div>
                    <div className="text-sm text-slate-400 font-mono">
                      {totalRecords.toLocaleString()} total runs
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <History className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No search history yet. Generate your first star band above!</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Loading overlay for history */}
      {loadingHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#0a0a0f] p-6 rounded-xl flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
            <span className="text-white">Loading historical run...</span>
          </div>
        </div>
      )}

      {/* Star Table Modal */}
      {showStarTable && starTableData && (
        <StarTable
          stars={starTableData.stars}
          runName={starTableData.runName}
          birthDate={starTableData.birthDate}
          placeAddress={starTableData.placeAddress}
          onClose={closeStarTable}
        />
      )}
    </div>
  );
};

export default ZenithPage;
